import { i18n } from '@/locale/i18n'
import { QUESTION_STATUS } from '@/constants/marketBackendEnums'

/**
 * Crypto price up/down markets carry the Binance pair in `event_extra_data.pair`
 * (e.g. BTCUSDT). It is what drives the candlestick chart and the price-to-beat header.
 */
export const isCryptoPriceEvent = question => Boolean(question?.event_extra_data?.pair)

/** Binance symbol for the market's pair, normalized for the websocket stream and the REST query. */
export const getCryptoPairSymbol = question => {
  const pair = question?.event_extra_data?.pair
  if (!pair) return null
  const symbol = String(pair).toLowerCase().replace(/[^a-z0-9]/g, '')
  return symbol || null
}

/**
 * True once the market's outcome is decided or void: resolved or cancelled. `status` alone
 * decides it, as the sports headers do, because the backend flips `is_wagerable` and
 * `resolution` a moment after the status and the card must not show a live price in between.
 */
export const isCryptoMarketSettled = question =>
  question?.status === QUESTION_STATUS.RESOLVED || question?.status === QUESTION_STATUS.CANCELLED

/** Price to beat encoded in the slug, used when `event_extra_data.price` is not set. */
const extractPriceToBeatFromSlug = question => {
  const slug = question?.slug
  if (typeof slug !== 'string') return null
  const match = slug.match(/price-to-beat-(\d+)/)
  return match?.[1] ? parseInt(match[1], 10) / 100 : null
}

const toFiniteNumber = raw => {
  const value = raw === null || raw === undefined || raw === '' ? NaN : Number(raw)
  return Number.isFinite(value) ? value : null
}

/**
 * Price to beat from `event_extra_data.price` (daily markets) or `open_price` (hourly and
 * minute markets), then the slug. Null when the market carries none: it is drawn as a headline
 * figure and as the chart's reference line, so a stand-in number would read as a real target.
 */
export const getPriceToBeat = question => {
  const extra = question?.event_extra_data
  return toFiniteNumber(extra?.price) ?? toFiniteNumber(extra?.open_price) ?? extractPriceToBeatFromSlug(question)
}

/** Final price of a resolved market, when the backend recorded one. */
export const getCryptoClosePrice = question => toFiniteNumber(question?.event_extra_data?.close_price)

/** True when the market has a pair and a price to beat, which the price-led card and row need. */
export const hasCryptoPriceTarget = question =>
  Boolean(getCryptoPairSymbol(question)) && Number.isFinite(getPriceToBeat(question))

/** Pairs are quoted in USD stablecoins, so prices read as dollars with cents, as the chart shows them. Null for no price. */
export const formatCryptoPrice = value =>
  Number.isFinite(value) ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null

const getFrequency = question => String(question?.event_extra_data?.frequency || '').toLowerCase()

/** Minutes of a minute-frequency market ("5min", "15 min"), else null. */
const getWindowMinutes = question => getFrequency(question).match(/^(\d+)\s*min/)?.[1] ?? null

/** Whole hours of an hourly market from `timeframe`; 1 when it is missing or not a positive integer. */
const getWindowHours = question => {
  const hours = toFiniteNumber(question?.event_extra_data?.timeframe)
  return Number.isInteger(hours) && hours > 0 ? hours : 1
}

/**
 * Short label for the market's window, from `event_extra_data.frequency` / `timeframe`:
 * "Today" for daily markets, "1 hour" for hourly, "5 min" / "15 min" for minute markets.
 */
export const getCryptoWindowLabel = question => {
  const frequency = getFrequency(question)
  if (frequency === 'daily') return i18n('question_card.crypto_window_today')
  if (frequency === 'hourly') {
    const hours = getWindowHours(question)
    return i18n(hours === 1 ? 'question_card.crypto_window_hours' : 'question_card.crypto_window_hours_plural', { hours })
  }
  const minutes = getWindowMinutes(question)
  return minutes ? i18n('question_card.crypto_window_minutes', { minutes }) : null
}

/** Window as a title prefix: "Daily", "Hourly", "4 hours", "5 min". */
export const getCryptoWindowPrefix = question => {
  const frequency = getFrequency(question)
  if (frequency === 'daily') return i18n('question_card.crypto_window_daily')
  if (frequency === 'hourly') {
    const hours = getWindowHours(question)
    return hours === 1
      ? i18n('question_card.crypto_window_hourly')
      : i18n('question_card.crypto_window_hours_plural', { hours })
  }
  const minutes = getWindowMinutes(question)
  return minutes ? i18n('question_card.crypto_window_minutes', { minutes }) : null
}

const QUOTE_SYMBOLS = /(USDT|USDC|BUSD|USD)$/

const ASSET_NAMES = {
  BTC: 'Bitcoin',
  ETH: 'Ethereum',
  SOL: 'Solana',
  BNB: 'BNB',
  XRP: 'XRP',
  ADA: 'Cardano',
  DOGE: 'Dogecoin',
}

/** Ticker of the pair's base asset, e.g. BTCUSDT -> BTC. Null when the market has no pair. */
export const getCryptoAssetTicker = question => {
  const pair = question?.event_extra_data?.pair
  if (!pair) return null
  return String(pair).toUpperCase().replace(/\s+/g, '').replace(QUOTE_SYMBOLS, '') || null
}

/**
 * Display name of the pair's base asset, e.g. BTCUSDT -> Bitcoin. Unmapped assets fall back
 * to their ticker, so a new pair reads "SUI Price up or down" rather than naming the wrong coin.
 */
export const getCryptoAssetName = question => {
  const base = getCryptoAssetTicker(question)
  return base ? ASSET_NAMES[base] || base : null
}

/**
 * Heading for the market detail page. Crypto price markets get a short name: the backend title
 * spells out the deadline and the target price ("... today: $77,151.89"), both of which the page
 * now shows on their own — the countdown and the To Beat panel. SEO and share metadata keep the
 * full `question.title`.
 */
export const getMarketPageTitle = question => {
  const asset = getCryptoAssetName(question)
  return asset ? i18n('question_card.crypto_price_up_or_down', { asset }) : question?.title
}
